import React, { useState, useEffect, useMemo } from "react";
import { Button, DataTable, TableStatusDropdown, Tag, ActionButton } from "../../ui";
import { useNavigate } from "react-router-dom";
import { MDBContainer, MDBIcon, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalBody, MDBModalFooter, MDBSpinner, MDBInput } from "mdb-react-ui-kit";
import { api } from "../../../services";
import { decodeJwtPayload } from "../../../utils/tenantUserType";
import Toolbar from "../../Toolbar";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import BusinessInfoModal from "./BusinessInfoModal";

const COMPANY_STATUS_OPTIONS = ["Active", "Inactive", "Under Review"];
const STAFF_STATUS_OPTIONS = ["Active", "Inactive"];

function UserList() {
  const {t, i18n} = useTranslation("translation")
  const td = (key, options) => t(`adminAccountDelete.${key}`, options);
  const isRtl = i18n.language === "ar" || i18n.dir() === "rtl";
  const [viewMode, setViewMode] = useState("list");
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [tableSortColumn, setTableSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [confirmModal, setConfirmModal] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null);
  const [isToggling, setIsToggling] = useState(false);
  const [businessModalUser, setBusinessModalUser] = useState(null);
  const [deleteModalUser, setDeleteModalUser] = useState(null);
  const [deleteEligibility, setDeleteEligibility] = useState(null);
  const [deleteUsernameConfirm, setDeleteUsernameConfirm] = useState("");
  const [isCheckingDeleteEligibility, setIsCheckingDeleteEligibility] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const navigate = useNavigate();

  /** Admin user list: add-on toggles apply to restaurant admins (`company`), not staff. */
  const isRestaurantStaff = (u) =>
    String(u?.user_type || "").toLowerCase() === "staff";

  const orderStatuses = [
    { id: "All", name: `${t("all")}` },
    { id: "Active", name: `${t("active")}` },
    { id: "Inactive", name: `${t("inactive")}` },
    { id: "Under Review", name: `${t("underreview")}` },
  ];

  useEffect(() => {
    async function fetchUsers() {
      try {
        const token = localStorage.getItem("adminToken");
        const response = await api.get("/admin/users", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const restaurantAdminUsers = response.data.users.filter(
          (user) => String(user?.user_type || "").toLowerCase() === "company"
        );
        setUsers(restaurantAdminUsers);
      } catch (error) {
        console.error(
          `${t("Error fetching users:")}`,
          error.response?.data || error.message
        );
      }
    }
    fetchUsers();
  }, []);

  const getStatusLabel = (status) => {
    if (status === "Active") return t("active");
    if (status === "Inactive") return t("inactive");
    if (status === "Under Review") return t("underreview");
    if (status === "PendingVerification") return t("pendingverification");
    return status;
  };

  const getStatusVariant = (status) => {
    if (status === "Active") return "success";
    if (status === "Inactive") return "neutral";
    if (status === "Under Review") return "warning";
    return "neutral";
  };

  const getStatusOptions = (user) =>
    isRestaurantStaff(user) ? STAFF_STATUS_OPTIONS : COMPANY_STATUS_OPTIONS;

  const profileBadge = (user) => {
    const status = user.profileStatus || "unsubmitted";
    if (status === "approved") {
      return { variant: "success", label: t("adminBusinessInfo.approved") };
    }
    if (status === "rejected") {
      return { variant: "error", label: t("adminBusinessInfo.rejected") };
    }
    if (status === "update_pending") {
      return { variant: "warning", label: t("adminBusinessInfo.pendingApproval") };
    }
    if (status === "pending") {
      return { variant: "warning", label: t("adminBusinessInfo.pendingApproval") };
    }
    return { variant: "neutral", label: t("adminBusinessInfo.unsubmitted") };
  };

  const updateUserStatus = async (userId, newStatus) => {
    const target = users.find((u) => u._id === userId);
    if (
      target &&
      isRestaurantStaff(target) &&
      newStatus === "Under Review"
    ) {
      return;
    }
    try {
      const token = localStorage.getItem("adminToken");
      const response = await api.patch(
        `/admin/users/${userId}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user._id === userId
            ? { ...user, status: response.data.user.status }
            : user
        )
      );
    } catch (error) {
      console.error(
        "Error updating user status:",
        error.response?.data || error.message
      );
      const message =
        error.response?.data?.message ||
        t("Failed to update status. Please try again.");
      toast.error(message);
    }
  };

  const magicLogin = async (userId) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await api.post(
        `/admin/restaurant-login`,
        { id: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Admin magic login token", response.data);
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("active_role", "user");
      const payload = decodeJwtPayload(response.data.token);
      const userType = payload?.user?.user_type;
      if (userType) {
        localStorage.setItem("user_type", userType);
        localStorage.setItem(
          "user",
          JSON.stringify({
            id: payload?.user?.id,
            user_type: userType,
            restaurantId: payload?.user?.restaurantId || null,
          })
        );
      }
      
      // Trigger userLogin event to reconnect WebSocket
      console.log("🔄 Dispatching userLogin event...");
      window.dispatchEvent(new Event("userLogin"));
      console.log("✅ userLogin event dispatched");
      
      navigate("/dashboard", { state: { token: response.data.token } });
    } catch (error) {
      console.error(
        "Error navigating as user:",
        error.response?.data || error.message
      );
      alert(t("User under review / not active, not able to navigate"));
    }
  };

  // Toggle app availability API function
  const toggleAppAvailability = async (userId, availableAppId, enabled) => {
    try {
      const token = localStorage.getItem("adminToken");
      await api.post(
        "/admin/users/toggle-app-availability",
        {
          userId: userId,
          availableAppId: availableAppId,
          enabled: enabled,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Update local state using addonApps array
      setUsers((prevUsers) =>
        prevUsers.map((user) => {
          if (user._id !== userId) return user;

          // Update addonApps array if it exists
          const updatedAddonApps = user.addonApps?.map((app) =>
            app.appId === availableAppId ? { ...app, enabled } : app
          ) || [];

          // Also update legacy fields for backward compatibility
          const legacyUpdates = {};
          if (user.chat_agent_id === availableAppId) {
            legacyUpdates.chat_agent_enabled = enabled;
          }
          if (user.digital_catalog_id === availableAppId) {
            legacyUpdates.digital_catalog_enabled = enabled;
          }
          if (user.track_hub_id === availableAppId) {
            legacyUpdates.track_hub_enabled = enabled;
          }

          return {
            ...user,
            addonApps: updatedAddonApps,
            ...legacyUpdates,
          };
        })
      );
    } catch (error) {
      console.error(
        "Error toggling app availability:",
        error.response?.data || error.message
      );
      const apiMsg = error.response?.data?.message;
      alert(
        typeof apiMsg === "string" && apiMsg
          ? apiMsg
          : `${t("Failed to toggle app availability. Please try again.")}`
      );
    }
  };

  // Handle toggle with confirmation for disable
  const handleToggle = (userId, appType, newValue, appId) => {
    const target = users.find(
      (u) => u._id === userId || String(u._id) === String(userId)
    );
    if (isRestaurantStaff(target)) return;

    if (!newValue) {
      // Disabling - show confirmation modal
      setPendingToggle({ userId, appType, appId, enabled: newValue });
      setConfirmModal(true);
    } else {
      // Enabling - directly toggle
      toggleAppAvailability(userId, appId, newValue);
    }
  };

  // Confirm disable action
  const confirmToggle = async () => {
    if (pendingToggle) {
      setIsToggling(true);
      await toggleAppAvailability(
        pendingToggle.userId,
        pendingToggle.appId,
        pendingToggle.enabled
      );
      setIsToggling(false);
      setConfirmModal(false);
      setPendingToggle(null);
    }
  };

  const filteredUsers = useMemo(() => {
    const query = (searchQuery || "").trim().toLowerCase();
    return users
      .filter((user) => {
        if (!query) return true;
        const username = String(user?.username ?? "").toLowerCase();
        const email = String(user?.email ?? "").toLowerCase();
        return username.includes(query) || email.includes(query);
      })
      .filter((user) => {
        if (activeTab === "All") return true;
        return user.status === activeTab;
      });
  }, [users, searchQuery, activeTab]);

  const sortedUsers = useMemo(() => {
    const list = [...filteredUsers];
    const dir = sortDirection === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let av;
      let bv;
      switch (tableSortColumn) {
        case "email":
          av = (a.email || "").toLowerCase();
          bv = (b.email || "").toLowerCase();
          break;
        case "phone":
          av = (a.phone || "").toLowerCase();
          bv = (b.phone || "").toLowerCase();
          break;
        case "createdAt":
          av = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bv = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dir * (av - bv);
        case "status":
          av = (a.status || "").toLowerCase();
          bv = (b.status || "").toLowerCase();
          break;
        default:
          av = (a.username || "").toLowerCase();
          bv = (b.username || "").toLowerCase();
      }
      if (typeof av === "string") return dir * av.localeCompare(bv);
      return 0;
    });
    return list;
  }, [filteredUsers, tableSortColumn, sortDirection]);

  const handleBusinessInfoSaved = (payload) => {
    if (!businessModalUser) return;
    setUsers((prev) =>
      prev.map((user) =>
        user._id === businessModalUser._id
          ? {
              ...user,
              profileStatus: payload?.profileStatus || user.profileStatus,
              servicesUnlocked: payload?.servicesUnlocked,
              businessInfo: payload?.businessInfo || user.businessInfo,
            }
          : user
      )
    );
  };

  const closeDeleteModal = () => {
    setDeleteModalUser(null);
    setDeleteEligibility(null);
    setDeleteUsernameConfirm("");
    setIsCheckingDeleteEligibility(false);
    setIsDeletingAccount(false);
  };

  const openDeleteFlow = async (user) => {
    setDeleteModalUser(user);
    setDeleteEligibility(null);
    setDeleteUsernameConfirm("");
    setIsCheckingDeleteEligibility(true);

    try {
      const token = localStorage.getItem("adminToken");
      const response = await api.get(`/admin/users/${user._id}/deletion-eligibility`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteEligibility(response.data);
    } catch (error) {
      console.error("Error checking deletion eligibility:", error.response?.data || error.message);
      const message =
        error.response?.data?.message || td("eligibilityFailed");
      toast.error(message);
      closeDeleteModal();
    } finally {
      setIsCheckingDeleteEligibility(false);
    }
  };

  const confirmDeleteAccount = async () => {
    if (!deleteModalUser || !deleteEligibility?.eligible) return;

    const expectedUsername = String(deleteModalUser.username || "").trim();
    if (deleteUsernameConfirm.trim() !== expectedUsername) {
      toast.error(td("usernameMismatch"));
      return;
    }

    setIsDeletingAccount(true);
    try {
      const token = localStorage.getItem("adminToken");
      await api.delete(`/admin/users/${deleteModalUser._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers((prev) => prev.filter((user) => user._id !== deleteModalUser._id));
      toast.success(td("deleteSuccess"));
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting account:", error.response?.data || error.message);
      const message = error.response?.data?.message || td("deleteFailed");
      toast.error(message);
      if (error.response?.data?.blockers) {
        setDeleteEligibility((prev) => ({
          ...prev,
          eligible: false,
          blockers: error.response.data.blockers,
          summary: error.response.data.summary || prev?.summary,
        }));
      }
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const renderDeleteBlockers = () => {
    if (!deleteEligibility?.blockers?.length) return null;
    return (
      <ul className="admin-delete-modal-list">
        {deleteEligibility.blockers.map((blocker) => {
          if (blocker.code === "INTEGRATIONS_EXIST") {
            return (
              <li key={blocker.code}>
                {td("integrationsBlocker", { count: blocker.count })}
              </li>
            );
          }
          if (blocker.code === "ORDERS_EXIST") {
            return (
              <li key={blocker.code}>
                {td("ordersBlocker", { count: blocker.count })}
              </li>
            );
          }
          return null;
        })}
      </ul>
    );
  };

  const renderDeleteSummaryMeta = (summary, indented = false) => {
    if (!summary) return null;
    const metaClass = indented
      ? "admin-delete-modal-meta admin-delete-modal-meta--indented text-muted"
      : "admin-delete-modal-meta text-muted";
    return (
      <>
        <p className={metaClass}>
          {td("summaryStores", { count: summary.storeCount || 0 })}
        </p>
        <p className={metaClass}>
          {td("staffWillBeDeleted", { count: summary.staffCount || 0 })}
        </p>
      </>
    );
  };

  const renderStatusCell = (user) => {
    return (
      <TableStatusDropdown
        value={user.status}
        options={getStatusOptions(user)}
        getLabel={getStatusLabel}
        getVariant={getStatusVariant}
        onChange={(newStatus) => updateUserStatus(user._id, newStatus)}
        ariaLabel={`${t("editstatus")}: ${user.username}`}
      />
    );
  };

  const userColumns = useMemo(
    () => [
      {
        id: "username",
        header: t("name"),
        sortable: true,
        strong: true,
        render: (user) => user.username,
      },
      {
        id: "email",
        header: t("email"),
        sortable: true,
        render: (user) => user.email,
      },
      {
        id: "phone",
        header: t("phone"),
        sortable: true,
        render: (user) => user.phone,
      },
      {
        id: "createdAt",
        header: t("joiningdate"),
        sortable: true,
        render: (user) => new Date(user.createdAt).toLocaleDateString(),
      },
      {
        id: "status",
        header: t("status"),
        sortable: true,
        width: "196px",
        render: (user) => renderStatusCell(user),
      },
      {
        id: "businessInfo",
        header: t("adminBusinessInfo.title"),
        width: "280px",
        headerClassName: "admin-user-biz-th",
        className: "admin-user-biz-td",
        render: (user) => {
          const badge = profileBadge(user);
          const actionLabel = t("adminBusinessInfo.review");
          return (
            <div className="admin-user-biz-cell">
              <Tag variant={badge.variant}>{badge.label}</Tag>
              <ActionButton
                variant="edit"
                showIcon={false}
                onClick={(e) => {
                  e.stopPropagation();
                  setBusinessModalUser(user);
                }}
                aria-label={`${actionLabel} ${t("adminBusinessInfo.title")} — ${user.username}`}
              >
                {actionLabel}
              </ActionButton>
            </div>
          );
        },
      },
      {
        id: "orderAgent",
        header: t("orderAgent"),
        width: "130px",
        render: (user) => (
          <label
            className={`toggle${isRestaurantStaff(user) ? " toggle--staff-disabled" : ""}`}
            title={
              isRestaurantStaff(user)
                ? t("addonAvailabilityNotForStaff", {
                    defaultValue:
                      "Add-on availability applies to restaurant admins only, not staff users.",
                  })
                : undefined
            }
          >
            <input
              type="checkbox"
              id={`orderAgent-${user._id}`}
              disabled={isRestaurantStaff(user)}
              checked={
                user.addonApps?.find((app) => app.appType === "CHAT_AGENT")?.enabled ||
                user.chat_agent_enabled ||
                false
              }
              onChange={(e) => {
                const app = user.addonApps?.find((a) => a.appType === "CHAT_AGENT");
                const appId = app?.appId || user.chat_agent_id;
                handleToggle(user._id, "CHAT_AGENT", e.target.checked, appId);
              }}
            />
            <span className="slider" />
          </label>
        ),
      },
      {
        id: "digitalCatalog",
        header: t("digitalCatalog"),
        width: "130px",
        render: (user) => (
          <label
            className={`toggle${isRestaurantStaff(user) ? " toggle--staff-disabled" : ""}`}
            title={
              isRestaurantStaff(user)
                ? t("addonAvailabilityNotForStaff", {
                    defaultValue:
                      "Add-on availability applies to restaurant admins only, not staff users.",
                  })
                : undefined
            }
          >
            <input
              type="checkbox"
              id={`digitalCatalog-${user._id}`}
              disabled={isRestaurantStaff(user)}
              checked={
                user.addonApps?.find((app) => app.appType === "DIGITAL_CATALOG")?.enabled ||
                user.digital_catalog_enabled ||
                false
              }
              onChange={(e) => {
                const app = user.addonApps?.find((a) => a.appType === "DIGITAL_CATALOG");
                const appId = app?.appId || user.digital_catalog_id;
                handleToggle(user._id, "DIGITAL_CATALOG", e.target.checked, appId);
              }}
            />
            <span className="slider" />
          </label>
        ),
      },
      {
        id: "trackHub",
        header: t("trackHub"),
        width: "130px",
        render: (user) => (
          <label
            className={`toggle${isRestaurantStaff(user) ? " toggle--staff-disabled" : ""}`}
            title={
              isRestaurantStaff(user)
                ? t("addonAvailabilityNotForStaff", {
                    defaultValue:
                      "Add-on availability applies to restaurant admins only, not staff users.",
                  })
                : undefined
            }
          >
            <input
              type="checkbox"
              id={`trackHub-${user._id}`}
              disabled={isRestaurantStaff(user)}
              checked={
                user.addonApps?.find((app) => app.appType === "TRACK_HUB")?.enabled ||
                user.track_hub_enabled ||
                false
              }
              onChange={(e) => {
                const app = user.addonApps?.find((a) => a.appType === "TRACK_HUB");
                const appId = app?.appId || user.track_hub_id;
                handleToggle(user._id, "TRACK_HUB", e.target.checked, appId);
              }}
            />
            <span className="slider" />
          </label>
        ),
      },
      {
        id: "switch",
        header: t("switchuser"),
        width: "148px",
        nowrap: true,
        className: "admin-user-switch-td",
        render: (user) => (
          <a
            href="#!"
            onClick={(e) => {
              e.preventDefault();
              magicLogin(user._id);
            }}
            className="admin-user-switch-link d-inline-flex align-items-center text-black"
          >
            <MDBIcon fas icon="link" className="me-2" />
            <span>{t("switchaccount")}</span>
          </a>
        ),
      },
      {
        id: "deleteAccount",
        header: td("columnTitle"),
        width: "168px",
        nowrap: true,
        headerClassName: "admin-user-delete-th",
        className: "admin-user-delete-td",
        render: (user) => (
          <div className="admin-user-delete-cell">
            <ActionButton
              variant="delete"
              onClick={() => openDeleteFlow(user)}
              aria-label={`${td("deleteButton")} ${user.username}`}
            >
              {td("deleteButton")}
            </ActionButton>
          </div>
        ),
      },
    ],
    [t, td]
  );

  return (
    <div className="admin-page-root oh-page">
    <MDBContainer fluid className="content-area flex-grow-1">
      <h1 className="page-title-header">{t("userlist")}</h1>

      <div className="oh-page-section">
        <Toolbar
          showSearch
          searchPlaceHolder={t("Search Users")}
          searchQuery={searchQuery}
          handleSearch={(e) => setSearchQuery(e.target.value)}
          showFilter
          orderStatuses={orderStatuses}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          showViewMode={false}
        />
      </div>
      <style>{`
        /* Custom Toggle Styles */
        .toggle {
          position: relative;
          width: 46px;
          height: 24px;
          display: inline-block;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle .slider {
          position: absolute;
          inset: 0;
          cursor: pointer;
          background-color: #757575;
          border-radius: 999px;
          transition: background-color 0.2s ease;
          box-shadow: inset 0 0 0 1px #e0e0e0;
        }
        .toggle .slider::before {
          content: "";
          position: absolute;
          top: 3px;
          left: 3px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          transition: transform 0.2s ease;
        }
        .toggle input:checked + .slider {
          background-color: #083B4C;
          box-shadow: inset 0 0 0 1px #083B4C;
        }
        .toggle input:checked + .slider::before {
          transform: translateX(22px);
        }
        .toggle input:focus + .slider {
          box-shadow: 0 0 0 0.15rem rgba(7, 59, 76, 0.2);
        }
        .toggle input:disabled + .slider {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .toggle input:disabled {
          cursor: not-allowed;
        }
        .toggle.toggle--staff-disabled {
          pointer-events: none;
          cursor: not-allowed;
        }
        .admin-user-biz-th,
        .admin-user-biz-td {
          vertical-align: middle !important;
        }
        .admin-user-biz-th {
          white-space: nowrap;
        }
        .admin-user-biz-cell {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 8px;
          flex-wrap: nowrap;
          white-space: nowrap;
        }
        .admin-user-biz-cell .oh-tag {
          flex-shrink: 0;
        }
        .oh-table:not([dir="rtl"]) th.admin-user-delete-th,
        .oh-table:not([dir="rtl"]) td.admin-user-delete-td {
          padding-inline-start: 36px;
          padding-inline-end: 32px;
        }
        .admin-user-delete-cell {
          display: inline-flex;
          margin-inline-start: 12px;
        }
        .admin-user-switch-link {
          white-space: nowrap;
          flex-shrink: 0;
        }
        .admin-user-list-table .oh-table td,
        .admin-user-list-table .oh-table th {
          vertical-align: middle;
        }
        .admin-user-list-table .oh-table tbody td {
          padding-block: 14px;
        }
        .admin-delete-modal-copy {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .admin-delete-modal-message,
        .admin-delete-modal-meta {
          margin: 0;
        }
        .admin-delete-modal-list {
          margin: 0;
          padding-inline-start: 1.25rem;
        }
        .admin-delete-modal-meta--indented {
          padding-inline-start: 1.25rem;
        }
      `}</style>
      {viewMode === "list" && (
        <div className="oh-page-section">
        <DataTable
          tableId="admin-user-list"
          className="admin-user-list-table"
          title={t("Users")}
          selectionMode="index"
          columns={userColumns}
          data={sortedUsers}
          rowKey="_id"
          isRtl={isRtl}
          sort={{
            column: tableSortColumn,
            direction: sortDirection,
            onChange: ({ column, direction }) => {
              setTableSortColumn(column);
              setSortDirection(direction);
            },
          }}
          pagination={{
            pageSizeOptions: [10, 20, 50],
            showLabel: t("show"),
            ofLabel: t("of", { defaultValue: "of" }),
            resultsLabel: t("results", { defaultValue: "results" }),
            previousLabel: t("previous"),
            nextLabel: t("next"),
            isRtl,
          }}
        />
        </div>
      )}

      {/* Disable Confirmation Modal */}
      <MDBModal open={confirmModal} setShow={setConfirmModal} tabIndex="-1">
        <MDBModalDialog scrollable>
          <MDBModalContent>
            <MDBModalHeader className="d-flex align-items-center" style={{ justifyContent: 'flex-start' }}>
              <MDBIcon
                icon="exclamation-triangle"
                color="danger"
                className="me-2"
              />
              <span>{t("Disable Add-On")}</span>
            </MDBModalHeader>
            <MDBModalBody>
              {t("Disabling this Add-On will disable it for all enabled branches and the user will no longer be able to use it.")}
            </MDBModalBody>
            <MDBModalFooter>
              <Button
                color="secondary"
                onClick={() => {
                  setConfirmModal(false);
                  setPendingToggle(null);
                }}
                disabled={isToggling}
              >
                {t("cancel")}
              </Button>
              <Button
                color="danger"
                onClick={confirmToggle}
                disabled={isToggling}
              >
                {isToggling ? (
                  <>
                    <MDBSpinner size="sm" className="me-2" />
                    {t("Processing")}...
                  </>
                ) : (
                  t("Disable")
                )}
              </Button>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <MDBModal
        open={Boolean(deleteModalUser)}
        setShow={(open) => {
          if (!open) closeDeleteModal();
        }}
        tabIndex="-1"
      >
        <MDBModalDialog scrollable>
          <MDBModalContent>
            <MDBModalHeader className="d-flex align-items-center" style={{ justifyContent: "flex-start" }}>
              <MDBIcon
                icon="exclamation-triangle"
                color="danger"
                className="me-2"
              />
              <span>
                {deleteEligibility?.eligible === false
                  ? td("blockedTitle")
                  : td("confirmTitle")}
              </span>
            </MDBModalHeader>
            <MDBModalBody>
              {isCheckingDeleteEligibility ? (
                <div className="d-flex align-items-center">
                  <MDBSpinner size="sm" className="me-2" />
                  <span>{t("Processing")}...</span>
                </div>
              ) : deleteEligibility?.eligible === false ? (
                <div className="admin-delete-modal-copy">
                  {renderDeleteBlockers()}
                  {renderDeleteSummaryMeta(deleteEligibility.summary, true)}
                </div>
              ) : deleteEligibility?.eligible ? (
                <div className="admin-delete-modal-copy">
                  <p className="admin-delete-modal-message">
                    {td("confirmMessage", {
                      username: deleteModalUser?.username || "",
                      staffCount: deleteEligibility.summary?.staffCount || 0,
                    })}
                  </p>
                  {renderDeleteSummaryMeta(deleteEligibility.summary)}
                  <MDBInput
                    label={td("typeUsernameToConfirm", {
                      username: deleteModalUser?.username || "",
                    })}
                    value={deleteUsernameConfirm}
                    onChange={(e) => setDeleteUsernameConfirm(e.target.value)}
                  />
                </div>
              ) : null}
            </MDBModalBody>
            <MDBModalFooter>
              <Button
                color="secondary"
                onClick={closeDeleteModal}
                disabled={isDeletingAccount || isCheckingDeleteEligibility}
              >
                {deleteEligibility?.eligible === false ? td("close") : t("cancel")}
              </Button>
              {deleteEligibility?.eligible ? (
                <Button
                  color="danger"
                  onClick={confirmDeleteAccount}
                  disabled={
                    isDeletingAccount ||
                    isCheckingDeleteEligibility ||
                    deleteUsernameConfirm.trim() !== String(deleteModalUser?.username || "").trim()
                  }
                >
                  {isDeletingAccount ? (
                    <>
                      <MDBSpinner size="sm" className="me-2" />
                      {t("Processing")}...
                    </>
                  ) : (
                    td("deleteButton")
                  )}
                </Button>
              ) : null}
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <BusinessInfoModal
        show={Boolean(businessModalUser)}
        user={businessModalUser}
        onClose={() => setBusinessModalUser(null)}
        onSaved={handleBusinessInfoSaved}
      />
    </MDBContainer>
    </div>
  );
}

export default UserList;
